import {useMemo} from 'react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {capitalize} from '../utils/utils'
import {useTheme} from '@chakra-ui/react'

/**
 * Hook to handle individual promotion display logic and bonus product fetching.
 * @param {Object} promotion - The promotion object containing styling and product data.
 * @param {boolean} [isEnabled=true] - Flag to enable/disable product fetching.
 * @param {Object} [theme] - The Chakra theme object.
 * @returns {Object} Returns styles (colorScheme, fontColor), related products, and loading state.
 */
const useSpecialOffer = (promotion, isEnabled = true, themeOverride) => {
    const chakraTheme = useTheme()
    const theme = themeOverride || chakraTheme

    // precompute ids in stringified form to trigger loading only when it changes
    const ids = useMemo(
        () => promotion?.c_discountProducts?.join(','),
        [promotion?.c_discountProducts]
    )

    const {data: products, isInitialLoading: loading} = useProducts(
        {
            parameters: {ids, allImages: true}
        },
        {enabled: !!ids && isEnabled}
    )

    // recompute colorScheme and fontColor only when c_colorWorld changes
    const style = useMemo(() => {
        const colorWorld = `stokke${capitalize(promotion?.c_colorWorld || 'gray')}`
        const colorScheme = theme.colors?.[colorWorld]?.mid ? colorWorld : 'stokkeGray'

        const fontColorException = ['stokkeGray', 'stokkeBeige'].includes(colorWorld)
        const fontColor = fontColorException
            ? `${colorScheme}.accessibleText`
            : `${colorScheme}.dark`

        return {colorScheme, fontColor}
    }, [promotion?.c_colorWorld, theme.colors])

    const hasSelectedBonusProduct = promotion?.productItem?.[0]?.itemId

    return useMemo(
        () => ({
            products,
            colorScheme: style.colorScheme,
            fontColor: style.fontColor,
            hasSelectedBonusProduct,
            hasBonusProducts: Boolean(ids),
            loading
        }),
        [products, style, hasSelectedBonusProduct, ids, loading]
    )
}

export default useSpecialOffer
